"use client";

/**
 * Initializes PostHog (browser) and provides it to the tree.
 *
 * Posture (decided with the customer-data sensitivity of /dashboard in mind):
 *   - Events route through the same-origin `/ingest` proxy (next.config
 *     rewrites) so ad-blockers don't drop them.
 *   - Session recording is OFF everywhere — the dashboard renders BOM data
 *     and we never want a screen replay of it.
 *   - Autocapture is ON, but `before_send` (scrubPostHogEvent) drops all
 *     /staff events and strips element text + URL queries from /dashboard
 *     events, so MPNs / part numbers / search terms never leave the browser.
 *   - Pageviews are captured manually (PostHogPageView) per App Router nav.
 *   - person_profiles=identified_only: anonymous marketing traffic is still
 *     measured (funnels/trends), but a stored profile is only created once a
 *     user is identified (on the dashboard).
 *
 * If NEXT_PUBLIC_POSTHOG_KEY is unset (local dev, preview without a key),
 * this is a transparent pass-through — nothing initializes, nothing sends.
 */

import * as React from "react";
import posthog, { type CaptureResult } from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { scrubPostHogEvent } from "@/lib/analytics/posthog-scrub";
import { PostHogPageView } from "./posthog-pageview";

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://us.i.posthog.com";
const UI_HOST = POSTHOG_HOST.includes("eu")
  ? "https://eu.posthog.com"
  : "https://us.posthog.com";

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  // Init during render (guarded), NOT in useEffect: React runs the child
  // PostHogPageView effect before a parent effect, so a useEffect init would
  // let the first $pageview fire pre-init and get dropped. The __loaded guard
  // makes this idempotent across re-renders / strict mode.
  if (typeof window !== "undefined" && POSTHOG_KEY && !posthog.__loaded) {
    posthog.init(POSTHOG_KEY, {
      api_host: "/ingest", // same-origin proxy → real host via next.config rewrite
      ui_host: UI_HOST, // where the toolbar/links point
      capture_pageview: false, // captured manually in PostHogPageView
      capture_pageleave: true,
      autocapture: true, // full on marketing; dropped on /dashboard + /staff (before_send)
      // Off by default. PostHogPageView calls startSessionRecording() ONLY on
      // public marketing pages and stopSessionRecording() everywhere else, so
      // authenticated screens (BOM / PCN / MPN data) are never recorded (§3).
      disable_session_recording: true,
      // When recording (marketing pages only) mask every form input by default
      // — emails, contact + signup fields never enter a replay. Stripe-hosted
      // payment fields live on Stripe's own origin and are never in our DOM.
      session_recording: {
        maskAllInputs: true,
      },
      enable_heatmaps: false, // no heatmap DOM/coord capture (overrides remote config)
      capture_performance: false, // no $web_vitals (it nests $current_url w/ the BOM-filter query); overrides remote config
      capture_exceptions: false, // errors go to Sentry, not PostHog; overrides remote config
      disable_capture_url_hashes: true, // never capture URL fragments anywhere
      person_profiles: "identified_only",
      before_send: (event: CaptureResult | null) =>
        scrubPostHogEvent(event, window.location.pathname) as CaptureResult | null,
    });
  }

  // No key → don't wrap; the app runs untouched.
  if (!POSTHOG_KEY) return <>{children}</>;

  return (
    <PHProvider client={posthog}>
      <PostHogPageView />
      {children}
    </PHProvider>
  );
}
